import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import cn from '../../../utils/cn';

export enum ButtonVariant {
  DEFAULT,
  PRIMARY,
  BRAND,
  GHOST,
  LINK,
  DESTRUCTIVE,
}

export enum ButtonSize {
  SM,
  MD,
  LG,
  XL,
  ICON,
}

export const buttonVariants = cva(
  [
    'inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md text-xs font-normal transition-[background-color,box-shadow]',
    'disabled:pointer-events-none',
    'focus:outline-none focus:ring-1 focus:ring-neutral-500',
    '[&_svg]:pointer-events-none [&>svg]:size-3 [&>svg]:shrink-0',
  ],
  {
    variants: {
      variant: {
        [ButtonVariant.DEFAULT]:
          'bg-white text-neutral-600 shadow hover:bg-neutral-50 ring-1 ring-neutral-300 disabled:bg-neutral-50 disabled:text-neutral-400',
        [ButtonVariant.PRIMARY]:
          'bg-white text-neutral-600 shadow hover:bg-neutral-50 ring-1 ring-neutral-300 disabled:bg-neutral-50 disabled:text-neutral-400 focus:ring-primary',
        [ButtonVariant.BRAND]:
          'bg-brand-500 text-white shadow hover:bg-brand-600 ring-1 ring-brand-500 hover:ring-brand-600',
        [ButtonVariant.GHOST]:
          'bg-transparent text-neutral-600 ring-1 ring-transparent hover:ring-neutral-300',
        [ButtonVariant.LINK]: 'text-neutral-600 underline-offset-4 hover:underline',
        [ButtonVariant.DESTRUCTIVE]:
          'bg-red-500 text-white shadow hover:bg-red-600 ring-1 ring-red-500 hover:ring-red-600',
      } satisfies Record<ButtonVariant, string>,
      size: {
        [ButtonSize.SM]: 'px-2 py-1.5',
        [ButtonSize.MD]: 'px-2.5 py-2',
        [ButtonSize.LG]: 'px-3 py-2.5 text-sm',
        [ButtonSize.XL]: 'p-3 text-sm',
        [ButtonSize.ICON]: 'p-2',
      } satisfies Record<ButtonSize, string>,
    },
    defaultVariants: {
      variant: ButtonVariant.DEFAULT,
      size: ButtonSize.MD,
    },
  },
);

export type ButtonProps = React.ComponentPropsWithoutRef<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  };

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';

    return (
      <Comp
        className={cn(buttonVariants({ variant, size }), className)}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

export default Button;
